import { styled } from '@mui/material/styles';
import { Box, Card, Typography, Paper, Button, Chip } from '@mui/material';

// Container styles
export const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 1.5, 1, 1.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2, 2, 1, 2)
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2, 3, 0.5, 3)
  },
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '90vh',
  overflow: 'auto'
}));

export const HeaderContainer = styled(Box)({
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});

export const ContentGridContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1)
}));

export const SubmitButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 'auto',
  paddingTop: '8px',
  gap: '12px'
});

// Card styles - Making sure they're consistent
export const cardTransition = (theme) => ({
  transition: '0.3s',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  height: '100%',
  backgroundColor: '#fcfcfc',
  position: 'relative' // Added to support absolute positioning of elements
});

export const cardContentStyles = {
  p: 1.2,
  '&:last-child': { 
    pb: 1.2
  }
};

// Section title styles
export const sectionTitleStyles = (theme) => ({
  color: theme.palette.primary.main,
  fontSize: '1.05rem',
  borderBottomWidth: '2px',
  borderBottomColor: theme.palette.grey.light2,
  pb: 0.5,
  mb: 0.75,

});

// Typography styles
export const labelTextStyles = {
  fontSize: '0.85rem',
  mb: 0.25
};

export const statusChipStyles = (color) => ({
  height: 24,
  fontSize: '0.6rem',
  fontWeight: 500,
  '& .MuiChip-icon': {
    fontSize: '0.75rem',
    marginLeft: '4px'
  }
});

export const valueTextStyles = {
  fontSize: '0.9rem',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  flex: 1
};

// Chip styles
export const smallChipStyles = {
  height: '20px',
  fontSize: '0.75rem',
  mr: 0.5, 
  mb: 0.5
};

// Button styles
export const backButtonStyles = (theme) => ({
  textTransform: 'none',
  borderRadius: '8px',
  px: 3,
  '&:hover': {
    backgroundColor: '#ffffff',
    color: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  }
});

export const tableHeaderStyles = theme => ({
  backgroundColor: theme.palette.grey[100], 
  position: 'sticky', 
  top: 0,
  zIndex: 10
});

export const tableHeaderCellStyles = theme => ({
  padding: '8px 12px',
  fontWeight: 600,
  fontSize: '0.75rem',
  textAlign: 'left',
  color: theme.palette.grey[700],
  borderBottom: `1px solid ${theme.palette.divider}`
});

export const tableCellStyles = theme => ({
  padding: '6px 8px',
  fontSize: '0.75rem',
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary
});

export const actionButtonStyles = (theme) => ({
  textTransform: 'none',
  borderRadius: '16px',
  px: 3,
  minWidth: 180,
  py: 1,
  fontWeight: 500,
  mr: 1,
  '&:hover': {
    backgroundColor: '#ffffff',
    color: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  }
});

export const addButtonStyles = (theme) => ({
  textTransform: 'none',
  borderRadius: '8px',
  height: '28px', // Fixed height
  fontSize: '0.75rem', // Consistent font size
  py: 1,
  px: 1,
  mb:2,
  minWidth: '100px',
  '&:hover': {
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.main,

  }
});

// Dialog styles
export const dialogButtonStyles = (theme) => ({
  textTransform: 'none',
  borderRadius: '8px',
  px: 2,
  minWidth: 100,  // Add minimum width to prevent button size change during loading
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500]
  }
});

export const cancelDialogButtonStyles = (theme) => ({
  textTransform: 'none',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.primary.main
  }
});

// Description box styles
export const descriptionPaperStyles = (theme) => ({
  p: 0.75,
  mt: 0,
  bgcolor: theme.palette.grey[50],
  overflowY: 'auto',
  flex: 1,
  maxHeight: '70px',
  minHeight: 0
});

// Product table styles
export const productTableContainerStyles = {
  width: '100%',
  overflowX: 'auto',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0
};

// Dialog styles
export const dialogTitleStyles = (theme) => ({
  bgcolor: 'white', 
  color: theme.palette.primary.main, 
  borderBottom: `1px solid ${theme.palette.divider}`
});

// Alert styles
export const infoAlertStyles = {
  mb: 1,
  py: 0.25
};

// No comment text style
export const noCommentStyles = (theme) => ({
  fontStyle: 'italic',
  color: theme.palette.grey[500],
  fontSize: '0.8rem'
});
